import { CaretDown, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/primitives/Button";
import { Dropdown } from "@/components/ui/overlays/Dropdown";
import type { TablePaginationProps } from "./DataTable.types";

function buildPageNumbers(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  if (current <= 3) return [1, 2, 3, "...", total];
  if (current >= total - 2) return [1, "...", total - 2, total - 1, total];
  const pages: (number | "...")[] = [1];
  if (current > 3) pages.push("...");
  for (let page = Math.max(2, current - 1); page <= Math.min(total - 1, current + 1); page += 1) {
    pages.push(page);
  }
  if (current < total - 2) pages.push("...");
  pages.push(total);
  return pages;
}

function formatPageSizeLabel(option: number, itemLabel: TablePaginationProps["itemLabel"]) {
  return typeof itemLabel === "string" || typeof itemLabel === "number" ? `${option} ${itemLabel}` : String(option);
}

export function TablePagination({
  page,
  totalPages,
  variant = "indexed",
  pageSize,
  totalItems,
  pageSizeOptions = [10, 15, 20, 25, 50],
  onPageChange,
  onPageSizeChange,
  itemLabel = "items",
  itemsPerPageLabel = "Items per page:",
  className,
  pageSizeControl = "select",
  showPageNavigation = true
}: TablePaginationProps) {
  const normalizedTotalPages = Math.max(1, totalPages);
  const pages = buildPageNumbers(page, normalizedTotalPages);
  const showIndexedPages = showPageNavigation && variant === "indexed";

  return (
    <nav className={cn("bfrs-flex bfrs-flex-wrap bfrs-items-center bfrs-justify-between bfrs-gap-3 bfrs-border-t bfrs-border-border bfrs-bg-surface-card bfrs-px-5 bfrs-py-[7px]", className)} aria-label="Table pagination">
      <div className="bfrs-flex bfrs-items-center bfrs-gap-2 bfrs-text-xs bfrs-text-text-muted">
        {onPageSizeChange && pageSize && (
          <>
            <span>{itemsPerPageLabel}</span>
            {pageSizeControl === "menu" ? (
              <Dropdown
                align="start"
                size="sm"
                trigger={
                  <Button variant="secondary" size="xs" rightIcon={<CaretDown size={10} />}>
                    {pageSize} {itemLabel}
                  </Button>
                }
                items={pageSizeOptions.map((option) => ({
                  label: `${option} ${itemLabel}`,
                  checked: option === pageSize,
                  onSelect: () => onPageSizeChange(option)
                }))}
              />
            ) : (
              <select
                value={pageSize}
                onChange={(event) => onPageSizeChange(Number(event.target.value))}
                className="bfrs-h-[26px] bfrs-min-w-[100px] bfrs-rounded-md bfrs-border bfrs-border-border bfrs-bg-surface-card bfrs-px-2 bfrs-text-xs bfrs-text-text-primary bfrs-outline-none focus-visible:bfrs-border-border-focus focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/20"
              >
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {formatPageSizeLabel(option, itemLabel)}
                  </option>
                ))}
              </select>
            )}
          </>
        )}
        {pageSizeControl !== "menu" && typeof totalItems === "number" && <span>{totalItems} {itemLabel}</span>}
      </div>
      {showPageNavigation && (
        <div className="bfrs-flex bfrs-items-center bfrs-gap-1">
          <Button aria-label="Previous page" variant="ghost" size="xs" disabled={page <= 1} leftIcon={<CaretLeft size={12} />} onClick={() => onPageChange(Math.max(1, page - 1))}>
            PREV
          </Button>
          {showIndexedPages && pages.map((entry, index) =>
            entry === "..." ? (
              <span key={`ellipsis-${index}`} className="bfrs-px-2 bfrs-text-xs bfrs-text-text-muted">...</span>
            ) : (
              <button
                key={entry}
                type="button"
                aria-current={entry === page ? "page" : undefined}
                aria-label={`Page ${entry}`}
                className={cn(
                  "bfrs-h-[26px] bfrs-min-w-[26px] bfrs-rounded-md bfrs-px-2 bfrs-text-xs bfrs-font-semibold bfrs-text-text-muted bfrs-outline-none bfrs-transition hover:bfrs-bg-surface-muted focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus",
                  entry === page && "bfrs-bg-text-secondary bfrs-text-primary-contrast hover:bfrs-bg-text-secondary"
                )}
                onClick={() => onPageChange(entry)}
              >
                {entry}
              </button>
            )
          )}
          <Button aria-label="Next page" variant="ghost" size="xs" disabled={page >= normalizedTotalPages} rightIcon={<CaretRight size={12} />} onClick={() => onPageChange(Math.min(normalizedTotalPages, page + 1))}>
            NEXT
          </Button>
        </div>
      )}
    </nav>
  );
}
